/**
 * Interface representing a disposable resource.
 */
export interface Disposable {
	dispose(): void;
}

/**
 * Type guard to check if a value is Disposable.
 */
export function isDisposable(value: any): value is Disposable {
	return typeof value === 'object' && value !== null && typeof value.dispose === 'function';
}

/**
 * A DisposableValue manages a value that can be updated and disposed of.
 */
export type DisposableValue<T> = {
	update: (nextValue: T) => void;
	value: () => T | null;
	dispose: () => void;
};

/**
 * Options for configuring DisposableValue behavior.
 */
export interface DisposableOptions<T> {
	/**
	 * Custom dispose function to clean up the value.
	 */
	disposeFn?: (value: T | null) => void;
	/**
	 * Whether to dispose the current value when updating to a new value.
	 * @default true
	 */
	disposeOnUpdate?: boolean;
}

/**
 * Creates a DisposableValue that manages the lifecycle of a value. When the value is updated or disposed,
 * the previous value is disposed of using the provided dispose function or its own dispose method if it implements Disposable.
 * @param initialValue Optional initial value to set.
 * @param optionsOrDisposeFn Optional options object or dispose function.
 * @returns A DisposableValue instance.
 **/
export function disposableValue<T>(
	initialValue?: T | null,
	optionsOrDisposeFn?: DisposableOptions<T> | ((value: T | null) => void),
): DisposableValue<T> {
	let currentValue: T | null = initialValue ?? null;

	// if second param is a function, treat it as disposeFn
	const options: DisposableOptions<T> =
		typeof optionsOrDisposeFn === 'function'
			? { disposeFn: optionsOrDisposeFn, disposeOnUpdate: true }
			: { disposeOnUpdate: true, ...optionsOrDisposeFn };

	const { disposeFn, disposeOnUpdate = true } = options;

	const disposeCurrentValue = () => {
		if (disposeFn) {
			disposeFn(currentValue);
		} else if (isDisposable(currentValue)) {
			currentValue.dispose();
		}
	};

	return {
		update: (nextValue: T | null) => {
			if (disposeOnUpdate) {
				disposeCurrentValue();
			}
			currentValue = nextValue;
		},
		value: () => currentValue,
		dispose: () => {
			disposeCurrentValue();
			currentValue = null;
		},
	};
}

/**
 * Creates a DisposableValue with a required dispose callback function. This is useful when you need
 * to ensure a specific cleanup function is always provided for managing the value's lifecycle.
 * @param disposeFn Required dispose function to clean up the value.
 * @param initialValue Optional initial value to set.
 * @param disposeOnUpdate Whether to dispose the current value when updating to a new value. Defaults to true.
 * @returns A DisposableValue instance with guaranteed custom disposal logic.
 **/
export function disposableCallback<T>(
	disposeFn: (value: T | null) => void,
	initialValue?: T | null,
	disposeOnUpdate: boolean = true,
): DisposableValue<T> {
	return disposableValue(initialValue, { disposeFn, disposeOnUpdate });
}

/**
 * Creates a DisposableValue that manages an array of values. When the array is updated or disposed,
 * each previous value is disposed of using the provided dispose function or its own dispose method if it implements Disposable.
 * @param initialValues Optional initial array of values.
 * @param optionsOrDisposeFn Optional options object or dispose function.
 * @returns A DisposableValue instance managing an array of values.
 **/
export function disposableValues<T extends unknown[]>(
	initialValues?: T | null,
	optionsOrDisposeFn?: DisposableOptions<T> | ((value: T | null) => void),
): DisposableValue<T> {
	let currentValues: T | null = initialValues ?? null;

	// if second param is a function, treat it as disposeFn
	const options: DisposableOptions<T> =
		typeof optionsOrDisposeFn === 'function'
			? { disposeFn: optionsOrDisposeFn, disposeOnUpdate: true }
			: { disposeOnUpdate: true, ...optionsOrDisposeFn };

	const { disposeFn, disposeOnUpdate = true } = options;

	const disposeCurrentValues = () => {
		if (disposeFn) {
			disposeFn(currentValues);
		} else {
			currentValues?.forEach((value) => {
				if (isDisposable(value)) {
					value.dispose();
				}
			});
		}
	};

	return {
		update: (nextValues: T | null) => {
			if (disposeOnUpdate) {
				disposeCurrentValues();
			}
			currentValues = nextValues;
		},
		value: () => currentValues,
		dispose: () => {
			disposeCurrentValues();
			currentValues = null;
		},
	};
}
